import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";
import type { InputMaybe, SaleTypeForTerm } from "@/lib/gql/__generated__/graphql";

import { FeatureIslandCategoryQuestionForMain } from "@/components/features/_layouts/island/main-with-category/components/category-main-question";
import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";
import {
	SALE_END_TIME_LIST,
	SALE_START_TIME_LIST,
} from "@/components/features/items/edit/_shared/constants/sale-term-time";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import { FormErrorMessage, FormItem, FormLabel } from "@/components/ui/form/_base/form";
import type { FormOptionType } from "@/components/ui/form/_types/type";
import { RadioGroupField } from "@/components/ui/form/fields/radio-field/radio-group";
import { SelectField } from "@/components/ui/form/fields/select-field";
import { DatePickerRange } from "@/components/ui/input/date-picker-range";
import { DatePicker } from "@/components/ui/input/date-picker-single";

const startTimeOptions = SALE_START_TIME_LIST.map((item, idx) => ({
	value: item.value,
	label: item.key,
	id: `saleStartTime_${idx}`,
}));

const endTimeOptions = SALE_END_TIME_LIST.map((item, idx) => ({
	value: item.value,
	label: item.key,
	id: `saleEndTime_${idx}`,
}));

type Props = {
	isEdited: boolean;
	hasError: boolean;

	isLimitedSale: boolean;

	saleTypeForTerm: ForNodeType<SaleTypeForTerm>;
	saleTypeForTermOptions: FormOptionType[];
	onSetSaleTypeForTerm: (input: string) => void;

	saleStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleStartTime: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEndTime: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetSaleTermWithTime: (input: {
		saleStart?: string;
		saleEnd?: string;
		saleStartTime?: string;
		saleEndTime?: string;
	}) => void;

	stayableStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	onSetStayableTerm: (input: DateRange) => void;

	guide?: string;
	title?: string;
};

export const QuestionSalesTermWithTime = memo(
	({
		isEdited,
		hasError,

		isLimitedSale,

		saleTypeForTerm,
		saleTypeForTermOptions,
		onSetSaleTypeForTerm,

		saleStart,
		saleEnd,
		saleStartTime,
		saleEndTime,
		onSetSaleTermWithTime,

		stayableStart,
		stayableEnd,
		onSetStayableTerm,

		title: _title,
		guide: _guide,
	}: Props) => {
		const title = _title || "プランの販売期間";
		const guide =
			_guide ||
			"プランの販売期間を設定することができます。期間限定を選択した場合、公開期間中に予約された宿泊可能期間に滞在する予約でのみ、プランが購入可能になります。<br />この設定は在庫管理から上書き可能です。";

		return (
			<div className={cn(isLimitedSale && "space-y-6")}>
				<FeatureIslandCategoryQuestionForMain
					required={false}
					title={title}
					guide={guide}
					isEdited={isEdited}
					hasError={hasError}
					inputChildren={
						<RadioGroupField
							value={saleTypeForTerm.value}
							options={saleTypeForTermOptions}
							handleValueChange={onSetSaleTypeForTerm}
							errorMessage={saleTypeForTerm.error}
						/>
					}
				/>
				<div className={cn(!isLimitedSale && "visually-hidden")}>
					<FeatureIslandCategoryQuestionForMain
						required={false}
						title={""}
						hasError={hasError}
						isEdited={isEdited}
						inputChildren={
							<div className="space-y-10 pb-4">
								<FormItem>
									<FormLabel>公開する期間</FormLabel>
									<div className="space-y-2">
										<div className="flex flex-wrap items-center gap-2">
											<DatePicker
												selected={saleStart?.value ?? ""}
												onSelect={(date) => {
													onSetSaleTermWithTime({
														saleStart: typeof date === "string" ? date : undefined,
														saleStartTime: saleStartTime?.value || "00:00",
													});
												}}
												className="w-auto"
											/>
											<span className="text-gray-600 text-sm">の</span>
											<SelectField
												value={saleStartTime?.value ?? ""}
												options={startTimeOptions}
												disabled={!saleStart?.value}
												handleValueChange={(time) => {
													onSetSaleTermWithTime({
														saleStartTime: time,
													});
												}}
												errorMessage={saleStartTime?.error}
												className="min-w-[90px]"
											/>
											<p>〜</p>
											<DatePicker
												selected={saleEnd?.value ?? ""}
												onSelect={(date) => {
													onSetSaleTermWithTime({
														saleEnd: typeof date === "string" ? date : undefined,
														saleEndTime: saleEndTime?.value || "23:59",
													});
												}}
												className="w-auto"
											/>
											<span className="text-gray-600 text-sm">の</span>
											<SelectField
												value={saleEndTime?.value ?? ""}
												options={endTimeOptions}
												disabled={!saleEnd?.value}
												handleValueChange={(time) => {
													onSetSaleTermWithTime({
														saleEndTime: time,
													});
												}}
												errorMessage={saleEndTime?.error}
												className="min-w-[90px]"
											/>
										</div>
									</div>
									<FormErrorMessage>{saleStart?.error || saleEnd?.error}</FormErrorMessage>
								</FormItem>
								<FormItem>
									<FormLabel>滞在可能な期間</FormLabel>
									<DatePickerRange
										selected={{
											from: stayableStart?.value ?? undefined,
											to: stayableEnd?.value ?? undefined,
										}}
										onSelect={onSetStayableTerm}
									/>
									<FormErrorMessage>{stayableStart?.error || stayableEnd?.error}</FormErrorMessage>
								</FormItem>
							</div>
						}
					/>
				</div>
			</div>
		);
	},
);

QuestionSalesTermWithTime.displayName = "QuestionSalesTermWithTime";
